import { Trans } from "@lingui/react/macro";
import {
  Shield,
  Eye,
  FileText,
  UserCheck,
  CreditCard,
  MapPin,
  MessageSquare,
  AlertTriangle,
  List,
  Database,
  Globe,
  Cookie,
} from "lucide-react";
import CompanyContactInfo from "../../components/legal/CompanyContactInfo";
import LegalPageLayout, {
  LegalSectionConfig,
} from "../../components/legal/LegalPageLayout";
import LegalSection from "../../components/legal/LegalSection";

export default function Privacy() {
  const sections: LegalSectionConfig[] = [
    {
      id: "general",
      label: <Trans>1. General & Controller</Trans>,
      icon: Shield,
    },
    { id: "hosting", label: <Trans>2. Hosting, Mail & Logs</Trans>, icon: Eye },
    {
      id: "cookies",
      label: <Trans>3. Local Storage & Session</Trans>,
      icon: Cookie,
    },
    { id: "auth", label: <Trans>4. User Accounts</Trans>, icon: UserCheck },
    {
      id: "profile",
      label: <Trans>5. Profiles & Media</Trans>,
      icon: FileText,
    },
    {
      id: "booking",
      label: <Trans>6. Bookings & Chat</Trans>,
      icon: MessageSquare,
    },
    {
      id: "payment",
      label: <Trans>7. Stripe Payments</Trans>,
      icon: CreditCard,
    },
    { id: "maps", label: <Trans>8. Maps & Geocoding</Trans>, icon: MapPin },
    {
      id: "sentry",
      label: <Trans>9. Error Tracking & Mail</Trans>,
      icon: AlertTriangle,
    },
    {
      id: "retention",
      label: <Trans>10. Data Retention</Trans>,
      icon: Database,
    },
    {
      id: "international",
      label: <Trans>11. International Transfers</Trans>,
      icon: Globe,
    },
    { id: "rights", label: <Trans>12. Your GDPR Rights</Trans>, icon: List },
  ];

  return (
    <LegalPageLayout
      title={<Trans>Privacy Policy</Trans>}
      description={
        <Trans>
          Information about how we collect, process, and protect your personal
          data in accordance with the General Data Protection Regulation (GDPR /
          DSGVO).
        </Trans>
      }
      icon={Shield}
      sections={sections}
      tableOfContentsLabel={<Trans>Table of Contents</Trans>}
    >
      <LegalSection
        id="general"
        title={<Trans>1. General Information & Controller</Trans>}
        icon={Shield}
      >
        <p>
          <Trans>
            We take the protection of your personal data very seriously. We
            treat your personal data confidentially and in accordance with the
            statutory data protection regulations of the European Union (GDPR)
            and the Federal Republic of Germany (BDSG).
          </Trans>
        </p>
        <p>
          <Trans>
            The controller responsible for data processing on this website (Art.
            4 No. 7 GDPR) is:
          </Trans>
        </p>
        <CompanyContactInfo email="privacy@soluud.de" />
        <p>
          <Trans>
            When you use this website, various personal data are collected.
            Personal data is data with which you can be personally identified.
            This Privacy Policy explains what data we collect and what we use it
            for. It also explains how and for what purpose this happens.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="hosting"
        title={<Trans>2. Hosting, Mail Server & Log Files</Trans>}
        icon={Eye}
      >
        <p>
          <Trans>
            Our platform and mail servers are hosted on rented infrastructure
            provided by Netcup GmbH, Daimlerstraße 9a, 76185 Karlsruhe, Germany.
            We have concluded a data processing agreement
            (Auftragsverarbeitungsvertrag) with Netcup in accordance with Art.
            28 GDPR.
          </Trans>
        </p>
        <p>
          <Trans>
            When you access our platform, we automatically collect and store
            technical information in server log files on these servers. This
            information is transmitted automatically by your browser and
            includes:
          </Trans>
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>
            <Trans>Browser type and browser version</Trans>
          </li>
          <li>
            <Trans>Operating system used</Trans>
          </li>
          <li>
            <Trans>Referrer URL (the page previously visited)</Trans>
          </li>
          <li>
            <Trans>Host name of the accessing computer (IP address)</Trans>
          </li>
          <li>
            <Trans>Time of the server request</Trans>
          </li>
        </ul>
        <p>
          <Trans>
            The legal basis for this processing is Art. 6(1)(f) GDPR. Our
            legitimate interest lies in the technically error-free presentation,
            security, and optimization of our website. A merger of this data
            with other data sources is not carried out. These log files are
            securely retained for 1 year to investigate anomalies and security
            incidents, after which they are automatically deleted.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="cookies"
        title={<Trans>3. Local Storage & Session Data (TDDDG)</Trans>}
        icon={Cookie}
      >
        <p>
          <Trans>
            To provide our service, we use "local storage" technologies in your
            browser. We only store data that is strictly technically necessary
            to provide the service you have requested, in accordance with § 25
            (2) No. 2 TDDDG.
          </Trans>
        </p>
        <p>
          <Trans>
            We do not use tracking cookies, marketing cookies, or third-party
            analytics cookies. The primary items we store are JSON Web Tokens
            (JWTs) which verify your login session securely. Without this
            technical storage, you would not be able to log in or book lessons.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="auth"
        title={<Trans>4. Registration & User Accounts</Trans>}
        icon={UserCheck}
      >
        <p>
          <Trans>
            When you register and create a user account on our platform:
          </Trans>
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>
            <Trans>
              We collect your email address, name, password hash, and assigned
              user roles.
            </Trans>
          </li>
          <li>
            <Trans>
              We issue secure session tokens (JWT) stored in your browser to
              maintain your active login session.
            </Trans>
          </li>
        </ul>
        <p>
          <Trans>
            This data is stored on our secure self-hosted database
            infrastructure on our rented servers. The processing of this data is
            necessary for the performance of our contract with you (Art. 6(1)(b)
            GDPR) to provide you access to your user account, and to protect the
            security of our platform (Art. 6(1)(f) GDPR).
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="profile"
        title={<Trans>5. Profile Data & Media Storage</Trans>}
        icon={FileText}
      >
        <p>
          <Trans>
            If you set up a profile as a music teacher, we store additional
            information: your instruments, genres, teaching locations,
            availability schedules, prices, and biography.
          </Trans>
        </p>
        <p>
          <Trans>
            We store user-uploaded assets (such as profile pictures, teaching
            material, and media files) in our self-hosted secure storage system
            on our rented servers. These media assets are served directly
            through our platform to enable users to view teacher profiles.
          </Trans>
        </p>
        <p>
          <Trans>
            The processing of profile data is carried out on the basis of
            contract fulfillment or pre-contractual measures (Art. 6(1)(b) GDPR)
            and, in the case of voluntary information or publicly displayed
            media, on the basis of your consent (Art. 6(1)(a) GDPR) or
            legitimate interest in marketing our platform services (Art. 6(1)(f)
            GDPR).
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="booking"
        title={<Trans>6. Bookings & Chat Communication</Trans>}
        icon={MessageSquare}
      >
        <p>
          <Trans>
            When you book lessons or manage subscriptions, we process booking
            details, scheduling records, and lesson logs to execute the lessons,
            track attendance, and facilitate coordinate-to-timezone conversions.
          </Trans>
        </p>
        <p>
          <Trans>
            We also provide a built-in chat system allowing students and
            teachers to communicate. We store chat messages and metadata to
            deliver them to the recipient and display your conversation history.
          </Trans>
        </p>
        <p>
          <Trans>
            The legal basis is the performance of the contract between student
            and teacher, and our service provider contract with you (Art.
            6(1)(b) GDPR).
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="payment"
        title={<Trans>7. Stripe Payment Processing</Trans>}
        icon={CreditCard}
      >
        <p>
          <Trans>
            To handle transactions securely, we integrate Stripe. For students,
            payments are processed via Stripe Checkout. For music teachers, we
            use Stripe Connect to facilitate payouts directly to their bank
            accounts.
          </Trans>
        </p>
        <p>
          <Trans>
            Stripe collects payment instrument details, transaction data, and
            KYC (Know-Your-Customer) details for payout onboarding. We do not
            store complete credit card details on our own servers.
          </Trans>
        </p>
        <p>
          <Trans>
            The data transfer to Stripe is based on Art. 6(1)(b) GDPR (contract
            performance) and to fulfill statutory legal duties regarding tax
            regulations and money laundering prevention (Art. 6(1)(c) GDPR).
          </Trans>
        </p>
        <p>
          <Trans>
            The recipient of this data is Stripe Payments Europe Ltd., 1 Grand
            Canal Street Lower, Grand Canal Dock, Dublin, Ireland. Stripe's
            processing is governed by their Data Processing Agreement (which
            incorporates Standard Contractual Clauses) to ensure compliance for
            any potential data transfers to Stripe, Inc. in the United States.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="maps"
        title={<Trans>8. Maps & Geocoding (MapBox & Nominatim)</Trans>}
        icon={MapPin}
      >
        <p>
          <Trans>
            To help students find local music teachers, we perform geocoding
            (converting address details to coordinates) and calculate geographic
            distances. We use MapBox as our primary geocoding and map service,
            and Nominatim (OpenStreetMap) as a fallback.
          </Trans>
        </p>
        <p>
          <Trans>
            To protect your privacy, map requests and geocoding queries are
            proxied through our own backend servers. This means your personal IP
            address is not transmitted directly to MapBox or OpenStreetMap when
            viewing maps or searching.
          </Trans>
        </p>
        <p>
          <Trans>
            This setup ensures your data is protected while providing
            location-based search functionality. The processing is based on Art.
            6(1)(f) GDPR, representing our legitimate interest in providing an
            intuitive platform without compromising your IP address.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="sentry"
        title={<Trans>9. Error Tracking (Sentry) & Notifications</Trans>}
        icon={AlertTriangle}
      >
        <p>
          <Trans>
            To improve the stability and performance of our application, we use
            Sentry, an error tracking tool. Sentry collects pseudonymized
            telemetry data and stack traces when client-side or server-side
            errors occur. This helps us debug crashes. This processing is based
            on our legitimate interest in maintaining system stability and
            security (Art. 6(1)(f) GDPR).
          </Trans>
        </p>
        <p>
          <Trans>
            Transactional emails (e.g. booking confirmations, chat
            notifications) are dispatched via our hosted mail servers. We
            process your email address and booking/chat details for these
            notifications, which is necessary for fulfilling our contractual
            services (Art. 6(1)(b) GDPR).
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="retention"
        title={<Trans>10. Data Retention Periods</Trans>}
        icon={Database}
      >
        <p>
          <Trans>
            We store your personal data only as long as it is necessary for the
            purposes for which it was collected or to comply with legal
            obligations.
          </Trans>
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>
            <Trans>
              <strong>Account Data:</strong> Retained until you delete your
              account. Upon deletion, your profile and media are removed.
            </Trans>
          </li>
          <li>
            <Trans>
              <strong>Financial & Booking Records:</strong> Due to statutory
              retention requirements under German commercial and tax law (HGB,
              AO), invoices and payment-related booking data are kept for up to
              10 years.
            </Trans>
          </li>
          <li>
            <Trans>
              <strong>Server Logs:</strong> Access logs are retained for 1 year
              before being automatically purged.
            </Trans>
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        id="international"
        title={<Trans>11. International Data Transfers</Trans>}
        icon={Globe}
      >
        <p>
          <Trans>
            Some of our service providers (e.g., Stripe, Sentry, MapBox) are
            located in or process data in the United States.
          </Trans>
        </p>
        <p>
          <Trans>
            Where personal data is transferred to the US, this is generally done
            based on the EU-US Data Privacy Framework (DPF), provided the
            recipient is certified under this framework. For other providers or
            in cases where the DPF does not apply, the transfer is safeguarded
            by Standard Contractual Clauses (SCCs) issued by the European
            Commission, combined with additional technical safeguards like
            server-side proxying where applicable.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="rights"
        title={<Trans>12. Your Rights under the GDPR</Trans>}
        icon={List}
      >
        <p>
          <Trans>
            As a data subject, you have the following rights under the General
            Data Protection Regulation:
          </Trans>
        </p>
        <ul className="list-disc space-y-2 pl-5 text-sm">
          <li>
            <strong>
              <Trans>Right to Access (Art. 15 GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You have the right to request confirmation as to whether or not
              your personal data is being processed, and access to details.
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>Right to Rectification (Art. 16 GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You have the right to obtain the correction of inaccurate personal
              data.
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>
                Right to Erasure / "Right to be Forgotten" (Art. 17 GDPR):
              </Trans>
            </strong>{" "}
            <Trans>
              You have the right to demand the erasure of your personal data
              under certain conditions.
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>Right to Restriction of Processing (Art. 18 GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You have the right to restrict the processing of your personal
              data under certain circumstances.
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>Right to Data Portability (Art. 20 GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You have the right to receive your personal data in a structured,
              commonly used, and machine-readable format.
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>Right to Object (Art. 21 GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You have the right to object to processing of your personal data
              based on legitimate interests (Art. 6(1)(f) GDPR).
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>Right to Withdraw Consent (Art. 7(3) GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You can withdraw your consent at any time for future processing
              operations.
            </Trans>
          </li>
          <li>
            <strong>
              <Trans>Right to Lodge a Complaint (Art. 77 GDPR):</Trans>
            </strong>{" "}
            <Trans>
              You have the right to lodge a complaint with a competent
              supervisory authority in your country of residence or federal
              state.
            </Trans>
          </li>
        </ul>
        <p className="mt-4 pt-2">
          <Trans>
            To exercise any of these rights, please contact us at any time using
            the contact details provided in Section 1 (email:
            privacy@soluud.de).
          </Trans>
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
